import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Assuming these components are available in your project
import ScrollToSectionButton from '../components/ScrollToSectionButton';

const HeroSection = () => {
  return (
    <section 
      className="relative flex items-center bg-beige-light"
      style={{ minHeight: 'var(--content-height)' }}
    >
      <div 
        className="absolute inset-0 bg-cover bg-top bg-no-repeat md:hidden"
        style={{ 
          backgroundImage: 'url("/images/webinar/webinar-leggeri-e-sfiammati.webp")',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-beige-light/75 to-beige-light"></div>
      </div>
      
      <div className="container relative z-10 mx-auto px-4 flex flex-col md:flex-row items-start">
        <div className="md:hidden flex flex-col justify-center w-full" style={{ minHeight: 'calc(var(--content-height) - 0rem)' }}>
          <div className="p-4 rounded-t-3xl">
            <h1 className="h1 text-beige-light mb-4">
              Riscopri il potere del cibo come alleato del tuo benessere
            </h1>
            <p className="body text-text-dark mt-4 mb-6">
              Lasciati guidare in un viaggio tra saperi antichi e scelte quotidiane: trasforma la tua cucina in uno spazio di cura, equilibrio e rinascita.
            </p>
            <ScrollToSectionButton 
              targetId="booking"
              className="flex h4 items-center justify-center px-6 py-5 w-full bg-beige-dark text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
            >
              Prenota il tuo posto all'incontro gratuito
              <ChevronDown className="ml-2" size={20} />
            </ScrollToSectionButton>
            <p className="text-center text-text-dark mt-4 font-medium w-full">
              I posti sono limitati – non perdere l'occasione!
            </p>
          </div>
        </div>

        <div className="hidden md:flex md:w-1/2 md:pr-8 flex-col">
          <h1 className="h1 text-green mb-4">
            Riscopri il potere del cibo come alleato del tuo benessere
          </h1>
          <p className="body text-text-dark mt-10 mb-10">
            Lasciati guidare in un viaggio tra saperi antichi e scelte quotidiane: trasforma la tua cucina in uno spazio di cura, equilibrio e rinascita.
          </p>
          <ScrollToSectionButton 
            targetId="booking"
            className="flex h4 items-center justify-center px-6 py-5 mt-4 bg-beige-dark text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
          >
            Prenota il tuo posto all'incontro gratuito
            <ChevronDown className="ml-2" size={20} />
          </ScrollToSectionButton>
          <p className="text-text-dark mt-4 font-medium">
            I posti sono limitati – non perdere l'occasione!
          </p>
        </div>
        <div className="hidden md:block md:w-1/2 mt-8 md:mt-0">
          <div className="rounded-15 aspect-[3/4] w-full max-w-md mx-auto overflow-hidden">
            <img 
              src="/images/webinar/webinar-leggeri-e-sfiammati.webp" 
              alt="Medicina Integrata e Alimentazione Consapevole"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const WebinarFeatures = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const features = [
    {
      id: 1,
      title: "Medicina Cinese & Macrobiotica",
      description: "Un'introduzione pratica all'equilibrio Yin-Yang e alla teoria dei 5 elementi: impara come l'energia stagionale influisce su organi, emozioni e vitalità, e come armonizzarti attraverso il cibo.",
      icon: "✅"
    },
    {
      id: 2,
      title: "Alimentazione Consapevole",
      description: "Quali alimenti scegliere in base alla stagione? Come ascoltare il corpo e adattare la dieta per favorire l'energia e il benessere profondo? Troverai risposte e nuove ispirazioni.",
      icon: "✅"
    },
    {
      id: 3,
      title: "Metodi di Purificazione Naturale",
      description: "Scopri come depurarti in modo dolce ma efficace: brodi curativi, ingredienti mirati e rituali semplici per liberare il corpo dalle tossine e alleggerire mente e spirito.",
      icon: "✅"
    }
  ];

  return (
    <div className="flex flex-col gap-6 mb-12">
      {features.map((feature) => (
        <div
          key={feature.id}
          className="p-6 bg-green rounded-15 shadow-md transition-all duration-300"
          onMouseEnter={() => setHoveredCard(feature.id)}
          onMouseLeave={() => setHoveredCard(null)}
          style={{
            transform: hoveredCard === feature.id ? 'translateY(-8px)' : 'translateY(0)',
            boxShadow: hoveredCard === feature.id ? '0 10px 25px rgba(0, 0, 0, 0.1)' : ''
          }}
        >
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3 text-beige-light">{feature.icon}</span>
            <h3 className="h4 text-beige-light">{feature.title}</h3>
          </div>
          <p className="body-tile text-text-light">{feature.description}</p>
        </div>
      ))}
    </div>
  );
};

const DetailsSection = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto mb-12">
      <div className="p-6 bg-green rounded-15 shadow-md text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-beige-light flex items-center justify-center mb-4">
            <span className="text-3xl text-green">🗓️</span>
          </div>
          <h3 className="h3 text-beige-light mb-3">Quando</h3>
          <p className="body-tile text-text-light mb-2">23 Aprile 2025</p>
          <p className="body text-beige-light font-bold">21:00 - 22:00</p>
          <p className="text-sm text-text-light mt-2">Durata: 1h 00m</p>
        </div>
      </div>
      <div className="p-6 bg-green rounded-15 shadow-md text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-beige-light flex items-center justify-center mb-4">
            <span className="text-3xl text-green">💻</span>
          </div>
          <h3 className="h3 text-beige-light mb-3">Come</h3>
          <p className="body-tile text-text-light mb-2">Google Meet</p>
          <p className="body text-beige-light font-bold">Online</p>
          <p className="text-sm text-text-light mt-2">Riceverai il link via email</p>
        </div>
      </div>
    </div>
  );
};


const WebinarSection = () => {
  return (
    <>
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="h2 text-green text-center mb-16">🌿 Cosa scoprirai durante il webinar</h2>
            <WebinarFeatures />
            <div className="p-6 bg-beige-light rounded-15 mb-12">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3 text-green">💬</span>
                <h3 className="h4 text-green">Spazio Q&A</h3>
              </div>
              <p className="body text-text-dark">
                Nella seconda parte dell'incontro risponderò alle vostre domande per accompagnarvi in questo percorso con più chiarezza e presenza.
              </p>
            </div>
            <DetailsSection />
          </div>
        </div>
      </section>

      <section id="booking" className="bg-green py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="h2 text-beige-light text-center mb-8">Prenota il tuo posto nell'incontro</h2>
            <div className="bg-white p-8 rounded-15 shadow-lg">
              <div style={{ height: "600px", width: "100%" }}>
                <iframe 
                  src="https://8n8.horoboro.com/form/219e83c9-1ec7-4875-b906-5bcfac9f684f"
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    border: "none",
                    borderRadius: "10px"
                  }}
                  allow="camera; microphone; autoplay; encrypted-media;"
                  title="Prenota il tuo posto"
                ></iframe>
              </div>
              <p className="text-center text-sm text-gray-600 mt-4">
                Inviando questo modulo accetti il trattamento dei dati secondo la nostra privacy policy.{" "}
                <a 
                  href="/privacy" 
                  className="text-green hover:text-accent underline transition-colors duration-300"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Clicca qui per leggere la nostra privacy policy
                </a>
              </p>
            </div>

            <div className="text-center mt-12">
              <p className="h4 text-beige-light">"Prendersi cura di sé attraverso il cibo è il primo passo per una vita più equilibrata"</p>
              <p className="body-tile text-text-light italic mt-4">
                Dott.ssa Alessandra Filzoli<br />
                Medico di Medicina Integrata e Medicina Tradizionale Cinese
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const WebinarLandingPage = () => {
  return (
    <div className="flex flex-col">
      <HeroSection />
      <WebinarSection />
    </div>
  );
};

export default WebinarLandingPage;